import React from 'react';
import { useAuth } from '@contexts/AuthContext';
import ConfirmActionModal from '@components/admin/ConfirmActionModal';
import { formatPersonName } from '@utils/stringUtils';
import { useContactUserStatusChange } from './useContactUserStatusChange';
import type { ContactUserDeactivation, UseDeactivateContactUserOptions } from '@app-types/contactUserDeactivation';

/**
 * Deactivate a Contact-User: the button's disabled state and tooltip, and the confirmation.
 * Shared by the edit form and the detail page header so both follow the same rules.
 */
export const useDeactivateContactUser = ({
  user,
  onDeactivated
}: UseDeactivateContactUserOptions): ContactUserDeactivation => {
  const { user: currentUser } = useAuth();
  const statusChange = useContactUserStatusChange({
    user,
    isActive: false,
    successMessage: 'User deactivated successfully',
    failureMessage: 'Failed to deactivate user',
    onChanged: onDeactivated
  });

  const isEditingSelf = user?.id !== undefined && user.id === currentUser?.id;
  const hasDependencies = Boolean(user?.dependencies?.hasDependencies);

  let tooltip = 'Deactivate User';
  if (isEditingSelf) tooltip = 'You cannot deactivate your own account';
  else if (hasDependencies) tooltip = 'Cannot deactivate: User has active dependencies';

  const confirmModal = (
    <ConfirmActionModal
      isOpen={statusChange.isConfirmOpen}
      onClose={statusChange.closeConfirm}
      onConfirm={statusChange.confirm}
      title="Deactivate User"
      message={`Are you sure you want to deactivate ${user ? formatPersonName(user) : 'this user'} (${user?.email})? They will no longer be able to sign in.`}
      isSubmitting={statusChange.isSubmitting}
      actionType="deactivate"
    />
  );

  return {
    isDisabled: statusChange.isSubmitting || isEditingSelf || hasDependencies,
    tooltip,
    openConfirm: () => statusChange.openConfirm(),
    confirmModal
  };
};
